import { useEffect, useState } from "react";
import { hasPromptReady, isIOS, isInstalled, triggerInstall, markUserConfirmedInstalled } from "@/lib/pwa";
import { DeviceMobile, ShareNetwork, X, Check } from "@phosphor-icons/react";

// Browsers known to support either the native install prompt or "Add to Home Screen".
function isInstallSupportedBrowser() {
  const ua = navigator.userAgent;
  const isAndroid = /android/i.test(ua);
  const isChromiumDesktop = /Chrome|Edg|OPR|Brave/.test(ua) && !/Mobile/.test(ua);
  return isIOS() || isAndroid || isChromiumDesktop;
}

function detectPlatform() {
  if (isIOS()) return "ios";
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "android";
  if (/Firefox/.test(ua)) return "firefox";
  if (/Edg/.test(ua)) return "edge";
  if (/Chrome/.test(ua)) return "chrome";
  if (/Safari/.test(ua)) return "safari";
  return "other";
}

/**
 * Always-discoverable install button.
 * - If the browser already has a deferred prompt → triggers the native install
 * - Otherwise shows platform-specific step-by-step instructions
 * - Hidden only if the app is already running as an installed PWA
 */
export default function InstallButton({ variant = "link", className = "" }) {
  const [hidden, setHidden] = useState(true);
  const [showSteps, setShowSteps] = useState(false);
  const [tickReady, setTickReady] = useState(0);

  useEffect(() => {
    if (isInstalled()) { setHidden(true); return; }
    if (!isInstallSupportedBrowser()) { setHidden(true); return; }
    setHidden(false);
    const tick = () => setTickReady((n) => n + 1);
    window.addEventListener("pwa:prompt-ready", tick);
    window.addEventListener("pwa:installed", () => setHidden(true));
    return () => window.removeEventListener("pwa:prompt-ready", tick);
  }, []);

  if (hidden) return null;

  const handleClick = async () => {
    if (hasPromptReady()) {
      const r = await triggerInstall();
      if (r.outcome === "accepted") setHidden(true);
      return;
    }
    setShowSteps(true);
  };

  const base = variant === "button"
    ? "inline-flex items-center gap-2 bg-[#f38838] hover:bg-[#e07523] text-white font-bold px-4 py-2 rounded-lg text-sm"
    : "inline-flex items-center gap-1.5 text-stone-600 hover:text-[#f38838] text-sm";

  const platform = detectPlatform();

  return (
    <>
      <button onClick={handleClick} className={`${base} ${className}`} data-testid="install-app-link">
        <DeviceMobile size={14} weight="duotone" /> Install app
      </button>

      {showSteps && (
        <div className="fixed inset-0 bg-stone-900/60 z-[70] flex items-end sm:items-center justify-center p-4" onClick={() => setShowSteps(false)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="install-steps">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-stone-900 inline-flex items-center gap-2">
                <DeviceMobile size={20} weight="duotone" className="text-[#f38838]" />
                Install the app
              </h3>
              <button onClick={() => setShowSteps(false)} className="text-stone-500"><X size={18} /></button>
            </div>

            {platform === "ios" && (
              <Steps items={[
                <>Tap the <strong className="inline-flex items-center gap-1"><ShareNetwork size={14} weight="bold" /> Share</strong> button in Safari's toolbar.</>,
                <>Scroll down and tap <strong>Add to Home Screen</strong>.</>,
                <>Tap <strong>Add</strong>. The app icon appears on your home screen.</>,
              ]} />
            )}

            {platform === "android" && (
              <Steps items={[
                <>Tap the <strong>⋮ menu</strong> (three dots, top-right of Chrome).</>,
                <>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong>.</>,
                <>Tap <strong>Install</strong>. The app icon appears on your home screen.</>,
              ]} />
            )}

            {(platform === "chrome" || platform === "edge") && (
              <>
                <p className="text-sm text-stone-600 mb-3">Look for the install icon in your address bar:</p>
                <Steps items={[
                  <>Look in the address bar — to the right of the URL there's usually an <strong>install icon</strong> (a monitor with a down-arrow).</>,
                  <>Click it and choose <strong>Install</strong>.</>,
                  <>If you don't see the icon, open the <strong>⋮ menu</strong> → <strong>Cast, save and share</strong> → <strong>Install Puppl…</strong></>,
                ]} />
                <p className="text-xs text-stone-500 mt-4">Chrome occasionally hides the icon until you've spent ~30 seconds on the site. Try clicking around for a moment, then check again.</p>
              </>
            )}

            {platform === "firefox" && (
              <p className="text-sm text-stone-600">Firefox doesn't currently support installing web apps on desktop — please use Chrome, Edge, or Safari to install.</p>
            )}

            {platform === "safari" && (
              <p className="text-sm text-stone-600">Safari on Mac doesn't support installing web apps yet — but Puppl works perfectly as a bookmark or pinned tab. On your iPhone or iPad it'll install with one tap from the Share menu.</p>
            )}

            {platform === "other" && (
              <p className="text-sm text-stone-600">Open Puppl in Chrome, Edge, Safari (iPhone), or Android Chrome to install the app.</p>
            )}

            <div className="mt-6 flex flex-col gap-2">
              <button
                onClick={() => { markUserConfirmedInstalled(); setShowSteps(false); setHidden(true); }}
                className="w-full bg-[#1b4a76] hover:bg-[#163a5f] text-white font-bold py-2.5 rounded-lg inline-flex items-center justify-center gap-2"
                data-testid="install-confirm"
              >
                <Check size={14} weight="bold" /> I've installed it — hide this
              </button>
              <button
                onClick={() => setShowSteps(false)}
                className="w-full text-sm text-stone-500 hover:text-stone-700 font-semibold py-1"
              >
                Not yet — close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Steps({ items }) {
  return (
    <ol className="space-y-3 text-sm text-stone-700">
      {items.map((content, i) => (
        <li key={i} className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-full bg-[#1b4a76] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
          <span>{content}</span>
        </li>
      ))}
    </ol>
  );
}
